import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { api, downloadOwnerPayrollSlip, openPdfBlobInNewTab, formatApiErrorUserMessage } from '../../api/client';
import {
  FiLayout,
  FiHome,
  FiLogOut,
  FiTrendingUp,
  FiCalendar,
  FiDollarSign,
  FiCreditCard,
  FiPieChart,
  FiMapPin,
  FiPlus,
  FiBriefcase,
  FiSearch,
  FiBarChart2,
  FiUsers,
  FiEdit2,
  FiChevronLeft,
  FiChevronRight,
  FiShield,
  FiDownload,
} from 'react-icons/fi';
import NotificationBell from '../../component/NotificationBell/NotificationBell';
import { formatDisplayName } from '../../utils/formatDisplayName';
import { FdPerPageField, normalizeFdPerPage } from '../../components/FdDataTableControls';
import './Dashboard.css';
import { luxisttConfirm } from '../../utils/luxisttSwal';
import {
  getPricingBreakdown,
  cautionToneClassName,
  ownerCanAdjustCautionFees,
  bookingHasCautionLine,
} from '../../utils/bookingPricing';
import { formatWholeNairaInputChange, parseWholeNairaToNumber } from '../../utils/moneyInputFormat';

const ROLE_ADMIN = 1;
const ROLE_OWNER = 2;
const ROLE_USER = 3;

const formatDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const BOOKING_SOURCE_LABELS = {
  online: 'Online',
  front_desk: 'Front desk',
  admin: 'Admin',
  owner: 'Owner',
};

function formatBookingSource(b) {
  const v = b?.booking_source;
  if (v == null || v === '') return '—';
  return BOOKING_SOURCE_LABELS[v] || String(v);
}

const BOOKING_PLATFORM_LABELS = {
  online: 'Luxistt.com',
  walk_in: 'Walk-in',
  airbnb: 'Airbnb',
  bookings: 'Bookings',
  expedia: 'Expedia',
  wakanow: 'Wakanow',
  others: 'Others',
};

function formatBookingPlatform(b) {
  const rel = b?.booking_platform;
  if (rel && typeof rel === 'object' && rel.name) return rel.name;
  if (b?.booking_platform_label) return b.booking_platform_label;
  const v = typeof rel === 'string' ? rel : null;
  if (v == null || v === '') return '—';
  return BOOKING_PLATFORM_LABELS[v] || String(v);
}

function ownerCanOfferGuestRefund(b) {
  return (
    b &&
    b.status === 'confirmed' &&
    b.payment_status === 'paid' &&
    !b.guest_refunded_at
  );
}

const paginate = (items, page, perPage) => {
  const pp = normalizeFdPerPage(perPage, 100);
  const total = items.length;
  const lastPage = Math.max(1, Math.ceil(total / pp));
  const currentPage = Math.min(Math.max(1, page), lastPage);
  const start = (currentPage - 1) * pp;
  return {
    items: items.slice(start, start + pp),
    meta: { current_page: currentPage, last_page: lastPage, per_page: pp, total },
  };
};

const PaginationFooter = ({ meta, onPageChange, loading }) => {
  if (!meta || meta.total === 0) return null;
  const { current_page, last_page, per_page, total } = meta;
  const start = (current_page - 1) * per_page + 1;
  const end = Math.min(current_page * per_page, total);
  return (
    <div className="table-pagination">
      <span className="pagination-info">Showing {start}–{end} of {total}</span>
      <div className="pagination-buttons">
        {last_page > 1 && (
          <>
            <button
              type="button"
              className="btn-ghost btn-pagination"
              disabled={current_page <= 1 || loading}
              onClick={() => onPageChange(Math.max(1, current_page - 1))}
              aria-label="Previous page"
            >
              <FiChevronLeft /> Previous
            </button>
            <span className="pagination-page">Page {current_page} of {last_page}</span>
            <button
              type="button"
              className="btn-ghost btn-pagination"
              disabled={current_page >= last_page || loading}
              onClick={() => onPageChange(current_page + 1)}
              aria-label="Next page"
            >
              Next <FiChevronRight />
            </button>
          </>
        )}
      </div>
    </div>
  );
};

const Dashboard = () => {
  const { user, logout, canManage, hasPermission } = useAuth();
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [report, setReport] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [cities, setCities] = useState([]);
  const [bookingPlatforms, setBookingPlatforms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [reportFrom, setReportFrom] = useState('');
  const [reportTo, setReportTo] = useState('');
  const [showPropertyModal, setShowPropertyModal] = useState(false);
  const [propertyForm, setPropertyForm] = useState({ name: '', city_id: '', address_street: '', address_postcode: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [propertySearch, setPropertySearch] = useState('');
  const [bookingFilterProperty, setBookingFilterProperty] = useState('');
  const [bookingFilterStatus, setBookingFilterStatus] = useState('all');
  const [bookingSearch, setBookingSearch] = useState('');
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [bookingsPage, setBookingsPage] = useState(1);
  const [bookingsMeta, setBookingsMeta] = useState(null);
  const [reportType, setReportType] = useState('summary');
  const [reportFromR, setReportFromR] = useState('');
  const [reportToR, setReportToR] = useState('');
  const [reportSummary, setReportSummary] = useState(null);
  const [reportBookings, setReportBookings] = useState([]);
  const [reportExpenses, setReportExpenses] = useState([]);
  const [reportPurchases, setReportPurchases] = useState([]);
  const [reportPayrollRows, setReportPayrollRows] = useState([]);
  const [reportPayrollSlipLoadingId, setReportPayrollSlipLoadingId] = useState(null);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [reportPropertyId, setReportPropertyId] = useState('');
  const [reportApartmentId, setReportApartmentId] = useState('');
  const [reportApartments, setReportApartments] = useState([]);
  const [showCreateBookingModal, setShowCreateBookingModal] = useState(false);
  const [bookingForm, setBookingForm] = useState({
    property_id: '', apartment_id: '', start_date: '', end_date: '', guests_adults: 1, guests_children: 0, total_price: '', booking_platform_id: '',
  });

  const defaultOwnerBookingPlatformId = useMemo(() => {
    const w = bookingPlatforms.find((p) => p.slug === 'walk_in');
    if (w) return String(w.id);
    return bookingPlatforms[0] ? String(bookingPlatforms[0].id) : '';
  }, [bookingPlatforms]);
  const [apartmentsForBooking, setApartmentsForBooking] = useState([]);
  const [users, setUsers] = useState([]);
  const [adminProperties, setAdminProperties] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({
    name: '', email: '', phone_number: '', password: '', password_confirmation: '', role_id: String(ROLE_OWNER), property_ids: [],
  });
  const [usersSearch, setUsersSearch] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [notificationKey, setNotificationKey] = useState(0);
  const [propertiesPage, setPropertiesPage] = useState(1);
  const [usersPage, setUsersPage] = useState(1);
  const [reportBookingsPage, setReportBookingsPage] = useState(1);
  const [reportExpensesPage, setReportExpensesPage] = useState(1);
  const [reportPurchasesPage, setReportPurchasesPage] = useState(1);
  const [dashboardTablePerPage, setDashboardTablePerPage] = useState(20);
  const [ownerBookingsPerPage, setOwnerBookingsPerPage] = useState(20);
  const [guestRefundModalBooking, setGuestRefundModalBooking] = useState(null);
  const [guestRefundAmount, setGuestRefundAmount] = useState('');
  const [guestRefundNote, setGuestRefundNote] = useState('');
  const [guestRefundSaving, setGuestRefundSaving] = useState(false);
  const [ownerCautionModalBooking, setOwnerCautionModalBooking] = useState(null);
  const [ownerCautionKind, setOwnerCautionKind] = useState('refund');
  const [ownerCautionAmount, setOwnerCautionAmount] = useState('');
  const [ownerCautionNote, setOwnerCautionNote] = useState('');
  const [ownerCautionSaving, setOwnerCautionSaving] = useState(false);

  const loadReport = async () => {
    const params = new URLSearchParams();
    if (reportFrom) params.set('from', reportFrom);
    if (reportTo) params.set('to', reportTo);
    const res = await api.get(`/owner/reports?${params.toString()}`);
    setReport(res);
  };

  useEffect(() => {
    const from = new Date();
    from.setDate(1);
    const to = new Date();
    const fromStr = from.toISOString().slice(0, 10);
    const toStr = to.toISOString().slice(0, 10);
    setReportFrom(fromStr);
    setReportTo(toStr);

    const load = async () => {
      try {
        const [propsRes, reportRes, citiesRes, bpRaw] = await Promise.all([
          api.get('/owner/properties'),
          api.get(`/owner/reports?from=${fromStr}&to=${toStr}`),
          api.get('/cities').catch(() => ({ data: [] })),
          api.get('/owner/booking-platforms').catch(() => null),
        ]);
        setProperties(propsRes.data || []);
        setReport(reportRes);
        setCities(citiesRes.data || []);
        const fromApi = bpRaw && Array.isArray(bpRaw.data) ? bpRaw.data : [];
        setBookingPlatforms((prev) => (fromApi.length > 0 ? fromApi : prev));
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    if (Array.isArray(user?.booking_platforms) && user.booking_platforms.length > 0) {
      setBookingPlatforms(user.booking_platforms);
    }
  }, [user?.booking_platforms]);

  const fetchBookings = () => {
    if (activeTab !== 'bookings') return;
    setBookingsLoading(true);
    const params = new URLSearchParams();
    if (bookingFilterProperty) params.set('property_id', bookingFilterProperty);
    if (bookingFilterStatus === 'upcoming') params.set('status', 'upcoming');
    if (bookingFilterStatus === 'past') params.set('status', 'past');
    params.set('page', String(bookingsPage));
    params.set('per_page', String(normalizeFdPerPage(ownerBookingsPerPage, 100)));
    api.get(`/owner/bookings?${params.toString()}`)
      .then((res) => {
        setBookings(Array.isArray(res.data) ? res.data : []);
        setBookingsMeta(res.meta || null);
      })
      .catch(() => {
        setBookings([]);
        setBookingsMeta(null);
      })
      .finally(() => setBookingsLoading(false));
  };

  useEffect(() => {
    if (bookingFilterProperty !== undefined || bookingFilterStatus !== undefined) {
      setBookingsPage(1);
    }
  }, [bookingFilterProperty, bookingFilterStatus]);

  useEffect(() => { setPropertiesPage(1); }, [propertySearch]);
  useEffect(() => { setUsersPage(1); }, [usersSearch]);
  useEffect(() => { setReportBookingsPage(1); setReportExpensesPage(1); setReportPurchasesPage(1); }, [reportType, reportPropertyId, reportApartmentId, reportFromR, reportToR]);

  useEffect(() => {
    setBookingsPage(1);
  }, [ownerBookingsPerPage]);

  useEffect(() => {
    setPropertiesPage(1);
    setUsersPage(1);
    setReportBookingsPage(1);
    setReportExpensesPage(1);
    setReportPurchasesPage(1);
  }, [dashboardTablePerPage]);

  useEffect(() => {
    if (activeTab === 'bookings') fetchBookings();
  }, [activeTab, bookingFilterProperty, bookingFilterStatus, bookingsPage, ownerBookingsPerPage]); // eslint-disable-line react-hooks/exhaustive-deps -- refetch when tab, filters or page change

  const reportParams = () => {
    const from = reportFromR || new Date().toISOString().slice(0, 10);
    const to = reportToR || new Date().toISOString().slice(0, 10);
    const params = new URLSearchParams({ from, to });
    if (reportPropertyId) params.set('property_id', reportPropertyId);
    if (reportApartmentId) params.set('apartment_id', reportApartmentId);
    return params.toString();
  };

  const fetchReports = () => {
    if (activeTab !== 'reports') return;
    const qs = reportParams();
    setReportsLoading(true);
    if (reportType === 'summary' || reportType === 'profit_loss') {
      api.get(`/owner/reports?${qs}`)
        .then((r) => setReportSummary(r))
        .catch(() => setReportSummary(null))
        .finally(() => setReportsLoading(false));
    } else if (reportType === 'bookings') {
      api.get(`/owner/bookings?${qs}`)
        .then((r) => setReportBookings(r.data || []))
        .catch(() => setReportBookings([]))
        .finally(() => setReportsLoading(false));
    } else if (reportType === 'expenses') {
      api.get(`/owner/reports/expenses?${qs}`)
        .then((r) => setReportExpenses(r.data || []))
        .catch(() => setReportExpenses([]))
        .finally(() => setReportsLoading(false));
    } else if (reportType === 'purchases') {
      api.get(`/owner/reports/purchases?${qs}`)
        .then((r) => setReportPurchases(r.data || []))
        .catch(() => setReportPurchases([]))
        .finally(() => setReportsLoading(false));
    } else if (reportType === 'payroll') {
      if (!reportPropertyId) {
        setReportPayrollRows([]);
        setReportsLoading(false);
        return;
      }
      api
        .get(`/owner/properties/${reportPropertyId}/payroll`)
        .then((r) => setReportPayrollRows(r.data || []))
        .catch(() => setReportPayrollRows([]))
        .finally(() => setReportsLoading(false));
    }
  };

  const onReportPropertyChange = (propertyId) => {
    setReportPropertyId(propertyId);
    setReportApartmentId('');
    if (!propertyId) {
      setReportApartments([]);
      return;
    }
    api.get(`/owner/properties/${propertyId}/apartments`)
      .then((r) => setReportApartments(r.data || []))
      .catch(() => setReportApartments([]));
  };

  const escapeCsv = (v) => {
    const s = String(v == null ? '' : v);
    if (s.includes('"') || s.includes(',') || s.includes('\n')) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };

  const exportReportCsv = () => {
    const from = reportFromR || '';
    const to = reportToR || '';
    const name = `report-${reportType}-${from}-${to}.csv`;
    let csv;
    if ((reportType === 'summary' || reportType === 'profit_loss') && reportSummary) {
      const headers = ['From', 'To', 'Revenue (paid)', 'Paid bookings', 'Expenses', 'Purchases', 'Profit'];
      const row = [reportSummary.from, reportSummary.to, reportSummary.revenue ?? 0, reportSummary.bookings_count ?? 0, reportSummary.expenses_total ?? 0, reportSummary.purchases_total ?? 0, reportSummary.profit ?? 0];
      csv = headers.map(escapeCsv).join(',') + '\n' + row.map(escapeCsv).join(',') + '\n';
    } else if (reportType === 'bookings') {
      const headers = ['Guest', 'Email', 'Property', 'Apartment', 'Start', 'End', 'Source', 'Platform', 'Amount'];
      const rows = reportBookings.map((b) => [
        b.user?.name ?? '',
        b.user?.email ?? '',
        b.apartment?.property?.name ?? '',
        b.apartment?.name ?? '',
        b.start_date ?? '',
        b.end_date ?? '',
        formatBookingSource(b),
        formatBookingPlatform(b),
        b.total_price ?? 0,
      ]);
      csv = headers.map(escapeCsv).join(',') + '\n' + rows.map((r) => r.map(escapeCsv).join(',')).join('\n') + (rows.length ? '\n' : '');
    } else if (reportType === 'expenses') {
      const headers = ['Date', 'Property', 'Apartment', 'Category', 'Amount', 'Description'];
      const rows = reportExpenses.map((ex) => [
        (ex.expense_date || '').slice(0, 10),
        ex.property?.name ?? '',
        ex.apartment?.name ?? '',
        ex.category ?? '',
        ex.amount ?? 0,
        ex.description ?? '',
      ]);
      csv = headers.map(escapeCsv).join(',') + '\n' + rows.map((r) => r.map(escapeCsv).join(',')).join('\n') + (rows.length ? '\n' : '');
    } else if (reportType === 'purchases') {
      const headers = ['Date', 'Property', 'Item', 'Category', 'Amount', 'Qty', 'Vendor'];
      const rows = reportPurchases.map((p) => [
        (p.purchase_date || '').slice(0, 10),
        p.property?.name ?? '',
        p.item_name ?? '',
        p.category ?? '',
        p.amount ?? 0,
        p.quantity ?? 1,
        p.vendor ?? '',
      ]);
      csv = headers.map(escapeCsv).join(',') + '\n' + rows.map((r) => r.map(escapeCsv).join(',')).join('\n') + (rows.length ? '\n' : '');
    } else if (reportType === 'payroll') {
      const headers = ['Period', 'Staff', 'Email', 'Gross', 'Deductions', 'Net', 'Paid', 'Reference'];
      const rows = reportPayrollRows.map((pr) => [
        `${pr.period_year}-${String(pr.period_month).padStart(2, '0')}`,
        pr.staff_name ?? '',
        pr.staff_email ?? '',
        pr.gross_amount_ngn ?? 0,
        pr.deductions_ngn ?? 0,
        pr.net_amount_ngn ?? 0,
        pr.paid_at ?? '',
        pr.payment_reference ?? '',
      ]);
      csv = headers.map(escapeCsv).join(',') + '\n' + rows.map((r) => r.map(escapeCsv).join(',')).join('\n') + (rows.length ? '\n' : '');
    } else {
      return;
    }
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = name;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  useEffect(() => {
    if (activeTab === 'reports') {
      const from = new Date();
      from.setDate(1);
      if (!reportFromR) setReportFromR(from.toISOString().slice(0, 10));
      if (!reportToR) setReportToR(new Date().toISOString().slice(0, 10));
      fetchReports();
    }
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (activeTab === 'reports') fetchReports();
  }, [activeTab, reportType, reportPropertyId, reportApartmentId, reportFromR, reportToR]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAdminUsers = () => {
    if (activeTab !== 'users' || !hasPermission('manage-users')) return;
    setUsersLoading(true);
    Promise.all([
      api.get('/admin/users'),
      api.get('/admin/properties'),
    ])
      .then(([usersRes, propsRes]) => {
        setUsers(usersRes.data || []);
        setAdminProperties(propsRes.data || []);
      })
      .catch(() => {
        setUsers([]);
        setAdminProperties([]);
      })
      .finally(() => setUsersLoading(false));
  };

  useEffect(() => {
    if (activeTab === 'users' && hasPermission('manage-users')) fetchAdminUsers();
  }, [activeTab, hasPermission]); // eslint-disable-line react-hooks/exhaustive-deps

  const openAddUser = () => {
    setEditingUser(null);
    setUserForm({ name: '', email: '', phone_number: '', password: '', password_confirmation: '', role_id: String(ROLE_OWNER), property_ids: [] });
    setShowUserModal(true);
    setError('');
  };

  const openEditUser = (u) => {
    setEditingUser(u);
    setUserForm({
      name: u.name,
      email: u.email,
      phone_number: u.phone_number || '',
      password: '',
      password_confirmation: '',
      role_id: String(u.role_id ?? ROLE_OWNER),
      property_ids: Array.isArray(u.property_ids) ? [...u.property_ids] : [],
    });
    setShowUserModal(true);
    setError('');
  };

  const closeUserModal = () => {
    setShowUserModal(false);
    setEditingUser(null);
  };

  const togglePropertyForUser = (propertyId) => {
    setUserForm((f) => ({
      ...f,
      property_ids: f.property_ids.includes(propertyId)
        ? f.property_ids.filter((id) => id !== propertyId)
        : [...f.property_ids, propertyId],
    }));
  };

  const handleSaveUser = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      if (editingUser) {
        const payload = { name: userForm.name, email: userForm.email, phone_number: userForm.phone_number };
        if (userForm.password) payload.password = userForm.password;
        if (userForm.password_confirmation) payload.password_confirmation = userForm.password_confirmation;
        if (editingUser.role === 'Owner') payload.property_ids = userForm.property_ids;
        await api.put(`/admin/users/${editingUser.id}`, payload);
        closeUserModal();
        fetchAdminUsers();
        setSuccessMessage('User updated successfully.');
        setNotificationKey((k) => k + 1);
        setTimeout(() => setSuccessMessage(''), 10_000);
      } else {
        const payload = {
          name: userForm.name,
          email: userForm.email,
          phone_number: userForm.phone_number,
          role_id: Number(userForm.role_id),
          property_ids: Number(userForm.role_id) === ROLE_OWNER ? userForm.property_ids : [],
        };
        if (userForm.password && userForm.password.trim()) {
          payload.password = userForm.password;
          payload.password_confirmation = userForm.password_confirmation;
        }
        const res = await api.post('/admin/users', payload);
        closeUserModal();
        fetchAdminUsers();
        const emailSent = res?.email_sent !== false;
        setSuccessMessage(emailSent
          ? 'User created successfully. A welcome email with a set-password link has been sent.'
          : 'User created successfully. Welcome email could not be sent (check mail config).');
        setNotificationKey((k) => k + 1);
        setTimeout(() => setSuccessMessage(''), 10_000);
      }
    } catch (err) {
      const msg = err?.message || 'Failed to save user';
      const details = err?.data?.errors
        ? Object.values(err.data.errors).flat().join(' ')
        : '';
      setError(details ? `${msg}: ${details}` : msg);
    } finally {
      setSaving(false);
    }
  };

  const handleCreateBooking = async (e) => {
    e.preventDefault();
    setError('');
    const totalCollected = parseWholeNairaToNumber(bookingForm.total_price);
    if (!Number.isFinite(totalCollected) || totalCollected < 0) {
      setError('Enter a valid amount collected (whole naira).');
      return;
    }
    setSaving(true);
    try {
      await api.post('/owner/bookings', {
        apartment_id: Number(bookingForm.apartment_id),
        start_date: bookingForm.start_date,
        end_date: bookingForm.end_date,
        guests_adults: Number(bookingForm.guests_adults),
        guests_children: Number(bookingForm.guests_children),
        total_price: totalCollected,
        booking_platform_id: Number(bookingForm.booking_platform_id || defaultOwnerBookingPlatformId),
      });
      setShowCreateBookingModal(false);
      setBookingForm({ property_id: '', apartment_id: '', start_date: '', end_date: '', guests_adults: 1, guests_children: 0, total_price: '', booking_platform_id: defaultOwnerBookingPlatformId });
      setApartmentsForBooking([]);
      if (activeTab === 'bookings') fetchBookings();
      setSuccessMessage('Booking created successfully.');
      setNotificationKey((k) => k + 1);
      setTimeout(() => setSuccessMessage(''), 10_000);
    } catch (err) {
      setError(err?.message || 'Failed to create booking');
    } finally {
      setSaving(false);
    }
  };

  const onBookingPropertyChange = (propertyId) => {
    setBookingForm((f) => ({ ...f, property_id: propertyId, apartment_id: '' }));
    if (!propertyId) {
      setApartmentsForBooking([]);
      return;
    }
    api.get(`/owner/properties/${propertyId}/apartments`)
      .then((r) => setApartmentsForBooking(r.data || []))
      .catch(() => setApartmentsForBooking([]));
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleReportRange = () => {
    setLoading(true);
    loadReport().finally(() => setLoading(false));
  };

  const handleAddProperty = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      const res = await api.post('/owner/properties', {
        name: propertyForm.name,
        city_id: Number(propertyForm.city_id),
        address_street: propertyForm.address_street,
        address_postcode: propertyForm.address_postcode || '',
      });
      setProperties((prev) => [res.data, ...prev]);
      setShowPropertyModal(false);
      setPropertyForm({ name: '', city_id: '', address_street: '', address_postcode: '' });
      setSuccessMessage('Property added successfully.');
      setNotificationKey((k) => k + 1);
      setTimeout(() => setSuccessMessage(''), 10_000);
    } catch (err) {
      setError(err?.message || 'Failed to add property');
    } finally {
      setSaving(false);
    }
  };

  if (!user) return null;

  const filteredProperties = propertySearch.trim()
    ? properties.filter(
        (p) =>
          (p.name || '').toLowerCase().includes(propertySearch.toLowerCase()) ||
          (p.city?.name || '').toLowerCase().includes(propertySearch.toLowerCase())
      )
    : properties;
  const { items: paginatedProperties, meta: propertiesMeta } = paginate(
    filteredProperties,
    propertiesPage,
    dashboardTablePerPage,
  );

  let filteredBookings = bookings;
  if (bookingSearch.trim()) {
    const q = bookingSearch.toLowerCase();
    filteredBookings = filteredBookings.filter(
      (b) =>
        (b.user?.name || '').toLowerCase().includes(q) ||
        (b.user?.email || '').toLowerCase().includes(q) ||
        (b.guest_name || '').toLowerCase().includes(q) ||
        (b.guest_email || '').toLowerCase().includes(q)
    );
  }

  const submitOwnerGuestRefund = async () => {
    const b = guestRefundModalBooking;
    if (!b?.id) return;
    const amt = parseWholeNairaToNumber(guestRefundAmount);
    if (!Number.isFinite(amt) || amt < 1) {
      setError('Enter a valid refund amount (whole naira, at least ₦1).');
      return;
    }
    const max = Number(b.total_price);
    if (!Number.isFinite(max) || max < 1) {
      setError('This booking has no collectable total to refund against.');
      return;
    }
    if (amt > max) {
      setError(`Refund cannot exceed the booking total (₦${max.toLocaleString('en-NG')}).`);
      return;
    }
    const ok = await luxisttConfirm({
      title: 'Refund guest?',
      text: `Cancel booking #${b.id} and mark payment refunded? The recorded total will be reduced by ₦${amt.toLocaleString('en-NG')}.`,
      confirmText: 'Yes, process refund',
      cancelText: 'Go back',
      danger: true,
      icon: 'warning',
    });
    if (!ok) return;
    setGuestRefundSaving(true);
    setError('');
    try {
      await api.post(`/owner/bookings/${b.id}/guest-refund`, {
        refund_amount: amt,
        ...(guestRefundNote.trim() ? { note: guestRefundNote.trim() } : {}),
      });
      setGuestRefundModalBooking(null);
      setGuestRefundAmount('');
      setGuestRefundNote('');
      setSuccessMessage(`Guest refund recorded for booking #${b.id}.`);
      fetchBookings();
    } catch (e) {
      setError(formatApiErrorUserMessage(e));
    } finally {
      setGuestRefundSaving(false);
    }
  };

  const submitOwnerCautionAdjustment = async () => {
    const b = ownerCautionModalBooking;
    if (!b?.id) return;
    const amt = parseWholeNairaToNumber(ownerCautionAmount);
    if (!Number.isFinite(amt) || amt < 1) {
      setError('Enter a valid amount (whole naira, at least ₦1).');
      return;
    }
    const pb = getPricingBreakdown(b);
    const summary =
      ownerCautionKind === 'refund'
        ? `Refund ₦${amt.toLocaleString('en-NG')} from the caution hold? The booking total will decrease.`
        : `Record forfeit / charge ₦${amt.toLocaleString('en-NG')}? Up to ₦${pb.caution_remainder.toLocaleString('en-NG')} can come from the caution pool; any excess adds to the total.`;
    const ok = await luxisttConfirm({
      title: ownerCautionKind === 'refund' ? 'Refund caution fee?' : 'Record caution forfeit / charge?',
      text: summary,
      confirmText: 'Confirm',
      cancelText: 'Go back',
      danger: ownerCautionKind !== 'refund',
      icon: 'warning',
    });
    if (!ok) return;
    setOwnerCautionSaving(true);
    setError('');
    try {
      await api.post(`/owner/bookings/${b.id}/caution-adjustment`, {
        kind: ownerCautionKind,
        amount: amt,
        ...(ownerCautionNote.trim() ? { note: ownerCautionNote.trim() } : {}),
      });
      setOwnerCautionModalBooking(null);
      setOwnerCautionKind('refund');
      setOwnerCautionAmount('');
      setOwnerCautionNote('');
      setSuccessMessage(`Caution fee updated for booking #${b.id}.`);
      fetchBookings();
    } catch (e) {
      setError(formatApiErrorUserMessage(e));
    } finally {
      setOwnerCautionSaving(false);
    }
  };

  const filteredUsers = usersSearch.trim()
    ? users.filter((u) => {
        const q = usersSearch.toLowerCase();
        return (
          (u.name || '').toLowerCase().includes(q) ||
          (u.email || '').toLowerCase().includes(q) ||
          (u.role || '').toLowerCase().includes(q)
        );
      })
    : users;
  const { items: paginatedUsers, meta: usersMeta } = paginate(filteredUsers, usersPage, dashboardTablePerPage);
  const { items: paginatedReportBookings, meta: reportBookingsMeta } = paginate(
    reportBookings,
    reportBookingsPage,
    dashboardTablePerPage,
  );
  const { items: paginatedReportExpenses, meta: reportExpensesMeta } = paginate(
    reportExpenses,
    reportExpensesPage,
    dashboardTablePerPage,
  );
  const { items: paginatedReportPurchases, meta: reportPurchasesMeta } = paginate(
    reportPurchases,
    reportPurchasesPage,
    dashboardTablePerPage,
  );

  const navItems = [
    { id: 'overview', label: 'Overview', icon: FiLayout },
    ...(hasPermission('properties-manage') ? [{ id: 'properties', label: 'Properties', icon: FiHome }] : []),
    ...((hasPermission('reports-view') || hasPermission('properties-manage')) ? [{ id: 'bookings', label: 'Bookings', icon: FiBriefcase }] : []),
    ...(hasPermission('reports-view') ? [{ id: 'reports', label: 'Reports', icon: FiBarChart2 }] : []),
    ...(hasPermission('manage-users') ? [{ id: 'users', label: 'Users', icon: FiShield }] : []),
  ];

  return (
    <div className="dashboard-layout">
      <aside className="dashboard-sidebar">
        <div className="sidebar-brand">
          <Link to="/">Luxistt</Link>
        </div>
        <nav className="sidebar-nav">
          {navItems.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              type="button"
              className={`sidebar-nav-item ${activeTab === id ? 'active' : ''}`}
              onClick={() => setActiveTab(id)}
            >
              <Icon className="sidebar-nav-icon" />
              <span>{label}</span>
            </button>
          ))}
        </nav>
        <div className="sidebar-footer">
          <div className="sidebar-user">
            {user.avatar_url ? (
              <img src={user.avatar_url} alt="" className="sidebar-user-avatar" width={40} height={40} />
            ) : null}
            <div className="sidebar-user-text">
              <span className="sidebar-user-name">{user.name}</span>
              <span className="sidebar-user-role">{user.role}</span>
            </div>
          </div>
          <button type="button" className="btn-logout" onClick={handleLogout}>
            <FiLogOut />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      <main className="dashboard-main">
        <div className="dashboard-main-inner">
        <header className="dashboard-header">
          <div className="dashboard-header-row">
            <div className="dashboard-header-text">
              <p className="dashboard-welcome">
                Welcome, {formatDisplayName(user.name, 'User')}
              </p>
              <h1 className="dashboard-title">
                {activeTab === 'overview' && 'Overview'}
                {activeTab === 'properties' && 'Properties'}
                {activeTab === 'bookings' && 'Bookings'}
                {activeTab === 'reports' && 'Reports'}
                {activeTab === 'users' && 'Users'}
              </h1>
              <p className="dashboard-subtitle">
                Manage Luxistt properties, bookings, and guests.
              </p>
            </div>
            <div className="dashboard-header-tools">
              <NotificationBell />
            </div>
          </div>
        </header>

        {successMessage && (
          <div className="dashboard-notification dashboard-notification--success" key={notificationKey} role="alert">
            <span>{successMessage}</span>
            <button type="button" className="dashboard-notification-dismiss" onClick={() => setSuccessMessage('')} aria-label="Dismiss">×</button>
          </div>
        )}

        {loading && activeTab === 'overview' ? (
          <div className="dashboard-loading">
            <div className="loading-spinner" />
            <p>Loading dashboard…</p>
          </div>
        ) : (
          <>
            {activeTab === 'overview' && (
              <section className="dashboard-overview">
                {report && (
                  <>
                    <div className="overview-toolbar">
                      <div className="report-dates">
                        <label>From <input type="date" value={reportFrom} onChange={(e) => setReportFrom(e.target.value)} /></label>
                        <label>To <input type="date" value={reportTo} onChange={(e) => setReportTo(e.target.value)} /></label>
                      </div>
                      <button type="button" className="btn-primary" onClick={handleReportRange}>Update</button>
                    </div>
                    <div className="overview-stats">
                      <div className="stat-card stat-card-revenue">
                        <div className="stat-card-icon"><FiDollarSign /></div>
                        <div className="stat-card-body">
                          <span className="stat-card-label">Revenue</span>
                          <span className="stat-card-value">₦{Number(report.revenue || 0).toLocaleString()}</span>
                          <span className="stat-card-meta">{report.from} – {report.to}</span>
                        </div>
                      </div>
                      <div className="stat-card">
                        <div className="stat-card-icon stat-card-icon-bookings"><FiCalendar /></div>
                        <div className="stat-card-body">
                          <span className="stat-card-label">Bookings</span>
                          <span className="stat-card-value">{report.bookings_count || 0}</span>
                        </div>
                      </div>
                      <div className="stat-card">
                        <div className="stat-card-icon stat-card-icon-expenses"><FiCreditCard /></div>
                        <div className="stat-card-body">
                          <span className="stat-card-label">Expenses</span>
                          <span className="stat-card-value">₦{Number(report.expenses_total || 0).toLocaleString()}</span>
                        </div>
                      </div>
                      <div className="stat-card">
                        <div className="stat-card-icon" style={{ color: '#8b5cf6' }}><FiCreditCard /></div>
                        <div className="stat-card-body">
                          <span className="stat-card-label">Purchases</span>
                          <span className="stat-card-value">₦{Number(report.purchases_total || 0).toLocaleString()}</span>
                        </div>
                      </div>
                      <div className="stat-card stat-card-profit">
                        <div className="stat-card-icon stat-card-icon-profit"><FiTrendingUp /></div>
                        <div className="stat-card-body">
                          <span className="stat-card-label">Profit</span>
                          <span className="stat-card-value">₦{Number(report.profit || 0).toLocaleString()}</span>
                        </div>
                      </div>
                      {report.payroll_paid_total != null && (
                        <div className="stat-card">
                          <div className="stat-card-icon stat-card-icon-bookings"><FiUsers /></div>
                          <div className="stat-card-body">
                            <span className="stat-card-label">Payroll paid</span>
                            <span className="stat-card-value">₦{Number(report.payroll_paid_total || 0).toLocaleString()}</span>
                            <span className="stat-card-meta">{report.payroll_slip_count ?? 0} slips · {report.active_staff_count ?? 0} active staff</span>
                          </div>
                        </div>
                      )}
                    </div>
                    {(() => {
                      const byType = report.expenses_by_type || report.expenses_by_category;
                      return byType && Object.keys(byType).length > 0 ? (
                      <div className="overview-expenses">
                        <h2 className="overview-expenses-title"><FiPieChart /> Expenses by category</h2>
                        <div className="overview-expenses-list">
                          {Object.entries(byType).map(([cat, total]) => (
                            <div key={cat} className="overview-expenses-item">
                              <span>{cat}</span>
                              <span>₦{Number(total).toLocaleString()}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      ) : null;
                    })()}
                  </>
                )}
              </section>
            )}

            {activeTab === 'properties' && (
              <section className="dashboard-section dashboard-section-table">
                {properties.length === 0 ? (
                  <div className="empty-state">
                    <FiMapPin className="empty-state-icon" />
                    <p className="empty-state-title">No properties yet</p>
                    <p className="empty-state-text">Add a property to start managing apartments and bookings.</p>
                    {canManage && (
                    <button type="button" className="btn-add" style={{ marginTop: '1rem' }} onClick={() => setShowPropertyModal(true)}>
                      <FiPlus /> Add property
                    </button>
                  )}
                  </div>
                ) : (
                  <div className="table-card">
                    <div className="table-toolbar">
                      <div className="filter-group">
                        <label htmlFor="property-search">
                          <FiSearch size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                          Search
                        </label>
                        <input
                          id="property-search"
                          type="search"
                          placeholder="Name or city…"
                          value={propertySearch}
                          onChange={(e) => setPropertySearch(e.target.value)}
                          aria-label="Search properties"
                        />
                      </div>
                      <FdPerPageField
                        id="dashboard-properties-per-page"
                        value={dashboardTablePerPage}
                        onChange={setDashboardTablePerPage}
                        maxAllowed={100}
                      />
                      <div className="toolbar-spacer" />
                      {canManage && (
                        <button type="button" className="btn-add" onClick={() => setShowPropertyModal(true)}>
                          <FiPlus /> Add property
                        </button>
                      )}
                    </div>
                    <div className="table-wrap">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Property</th>
                            <th className="col-num">Apartments</th>
                            <th className="col-num">Bookings</th>
                            <th className="col-actions">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredProperties.length === 0 ? (
                            <tr>
                              <td colSpan={4} style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                                {propertySearch.trim() ? 'No properties match your search.' : 'No properties.'}
                              </td>
                            </tr>
                          ) : (
                            paginatedProperties.map((p) => (
                              <tr key={p.id}>
                                <td>
                                  <span className="cell-primary">{p.name}</span>
                                  {p.city?.name && <span className="cell-muted">{p.city.name}</span>}
                                </td>
                                <td className="col-num">{p.apartments_count ?? 0}</td>
                                <td className="col-num">{p.bookings_count ?? 0}</td>
                                <td className="col-actions">
                                  <Link to={`/dashboard/properties/${p.id}`} className="btn-link">{canManage ? 'Manage' : 'View'} →</Link>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                    <PaginationFooter meta={propertiesMeta} onPageChange={setPropertiesPage} />
                  </div>
                )}
              </section>
            )}

            {activeTab === 'bookings' && (
              <section className="dashboard-section dashboard-section-table">
                <div className="table-card">
                  <div className="table-toolbar">
                    <div className="filter-group">
                      <label htmlFor="booking-search">Search guest</label>
                      <input
                        id="booking-search"
                        type="search"
                        placeholder="Name or email…"
                        value={bookingSearch}
                        onChange={(e) => setBookingSearch(e.target.value)}
                        aria-label="Search bookings by guest"
                      />
                    </div>
                    <div className="filter-group">
                      <label htmlFor="booking-property">Property</label>
                      <select
                        id="booking-property"
                        value={bookingFilterProperty}
                        onChange={(e) => setBookingFilterProperty(e.target.value)}
                        aria-label="Filter by property"
                      >
                        <option value="">All properties</option>
                        {properties.map((p) => (
                          <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="filter-group">
                      <label htmlFor="booking-status">Status</label>
                      <select
                        id="booking-status"
                        value={bookingFilterStatus}
                        onChange={(e) => setBookingFilterStatus(e.target.value)}
                        aria-label="Filter by status"
                      >
                        <option value="all">All</option>
                        <option value="upcoming">Upcoming</option>
                        <option value="past">Past</option>
                      </select>
                    </div>
                    <FdPerPageField
                      id="dashboard-owner-bookings-per-page"
                      value={ownerBookingsPerPage}
                      onChange={setOwnerBookingsPerPage}
                      maxAllowed={100}
                    />
                    <div className="toolbar-spacer" />
                    {canManage && (
                    <button type="button" className="btn-add" onClick={() => { setShowCreateBookingModal(true); setError(''); setBookingForm({ property_id: '', apartment_id: '', start_date: '', end_date: '', guests_adults: 1, guests_children: 0, total_price: '', booking_platform_id: defaultOwnerBookingPlatformId }); setApartmentsForBooking([]); }}>
                      <FiPlus /> Create booking
                    </button>
                  )}
                    {bookingsLoading && <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Loading…</span>}
                  </div>
                  <div className="table-wrap">
                    <table className="data-table data-table--bookings-rows">
                      <thead>
                        <tr>
                          <th>Guest</th>
                          <th>Apartment</th>
                          <th>Dates</th>
                          <th className="col-center">Status</th>
                          <th className="col-center">Source</th>
                          <th className="col-center">Platform</th>
                          <th className="col-amount">Sub total</th>
                          <th className="col-amount">Caution</th>
                          <th className="col-amount">Total</th>
                          {canManage ? <th className="col-actions">Actions</th> : null}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredBookings.length === 0 ? (
                          <tr>
                            <td colSpan={canManage ? 10 : 9} style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                              {bookingsLoading ? 'Loading…' : bookingSearch.trim() ? 'No bookings match your search.' : 'No bookings.'}
                            </td>
                          </tr>
                        ) : (
                          filteredBookings.map((b) => {
                            const statusColors = { pending: '#f59e0b', confirmed: '#3b82f6', checked_in: '#10b981', checked_out: '#6b7280', cancelled: '#ef4444' };
                            const statusLabels = { pending: 'Pending', confirmed: 'Confirmed', checked_in: 'Checked In', checked_out: 'Checked Out', cancelled: 'Cancelled' };
                            const st = b.status || 'confirmed';
                            const guestLine = `${b.guest_name || b.user?.name || '—'} · ${b.guest_email || b.user?.email || '—'}`;
                            const aptLine = b.apartment?.property?.name
                              ? `${b.apartment.property.name} · ${b.apartment?.name ?? '—'}`
                              : (b.apartment?.name ?? '—');
                            const pb = getPricingBreakdown(b);
                            const cautionCls = cautionToneClassName(pb.tone, 'booking-caution-tone');
                            return (
                            <tr key={b.id}>
                              <td className="dashboard-cell-ellipsis" title={guestLine}>
                                <span className="cell-primary">{b.guest_name || b.user?.name || '—'}</span>
                                <span className="cell-muted"> · {b.guest_email || b.user?.email || '—'}</span>
                              </td>
                              <td className="dashboard-cell-ellipsis" title={aptLine}>
                                <span className="cell-primary">{b.apartment?.name ?? '—'}</span>
                                {b.apartment?.property?.name ? <span className="cell-muted"> · {b.apartment.property.name}</span> : null}
                              </td>
                              <td>{formatDate(b.start_date)} – {formatDate(b.end_date)}</td>
                              <td className="col-center">
                                <span style={{ display: 'inline-block', padding: '0.2rem 0.5rem', borderRadius: '9999px', fontSize: '0.6875rem', fontWeight: 600, color: '#fff', background: statusColors[st] || '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                  {statusLabels[st] || st}
                                </span>
                              </td>
                              <td className="col-center" style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>{formatBookingSource(b)}</td>
                              <td className="col-center" style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>{formatBookingPlatform(b)}</td>
                              <td className="col-amount">₦{Number(pb.stay_subtotal).toLocaleString()}</td>
                              <td className={`col-amount ${cautionCls}`}>₦{Number(pb.caution_display).toLocaleString()}</td>
                              <td className="col-amount"><strong>₦{Number(pb.total).toLocaleString()}</strong></td>
                              {canManage ? (
                                <td className="col-actions">
                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', justifyContent: 'flex-end' }}>
                                    {ownerCanOfferGuestRefund(b) ? (
                                      <button
                                        type="button"
                                        className="dashboard-booking-action-pill dashboard-booking-action-pill--warn"
                                        onClick={() => {
                                          setGuestRefundModalBooking(b);
                                          setGuestRefundAmount('');
                                          setGuestRefundNote('');
                                          setError('');
                                        }}
                                      >
                                        Refund
                                      </button>
                                    ) : null}
                                    {bookingHasCautionLine(b) && ownerCanAdjustCautionFees(b) ? (
                                      <button
                                        type="button"
                                        className="dashboard-booking-action-pill dashboard-booking-action-pill--caution"
                                        onClick={() => {
                                          setOwnerCautionModalBooking(b);
                                          setOwnerCautionKind('refund');
                                          setOwnerCautionAmount('');
                                          setOwnerCautionNote('');
                                          setError('');
                                        }}
                                      >
                                        Caution fees
                                      </button>
                                    ) : null}
                                    {!ownerCanOfferGuestRefund(b) && !(bookingHasCautionLine(b) && ownerCanAdjustCautionFees(b)) ? (
                                      <span className="cell-muted" style={{ fontSize: '0.75rem' }}>—</span>
                                    ) : null}
                                  </div>
                                </td>
                              ) : null}
                            </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                  <PaginationFooter meta={bookingsMeta} onPageChange={setBookingsPage} loading={bookingsLoading} />
                </div>
              </section>
            )}

            {activeTab === 'reports' && (
              <section className="dashboard-section dashboard-section-table">
                <div className="table-card">
                  <div className="table-toolbar">
                    <div className="filter-group">
                      <label>Report</label>
                      <select value={reportType} onChange={(e) => setReportType(e.target.value)} aria-label="Report type">
                        <option value="summary">Revenue summary</option>
                        <option value="profit_loss">Profit &amp; loss</option>
                        <option value="bookings">Bookings report</option>
                        <option value="expenses">Expenses report</option>
                        <option value="purchases">Purchases report</option>
                        {hasPermission('payroll-manage') ? <option value="payroll">Payroll slips</option> : null}
                      </select>
                    </div>
                    <div className="filter-group">
                      <label>Property</label>
                      <select value={reportPropertyId} onChange={(e) => onReportPropertyChange(e.target.value)} aria-label="Filter by property">
                        <option value="">All properties</option>
                        {properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                      </select>
                    </div>
                    <div className="filter-group">
                      <label>Apartment</label>
                      <select value={reportApartmentId} onChange={(e) => setReportApartmentId(e.target.value)} aria-label="Filter by apartment" disabled={!reportPropertyId}>
                        <option value="">All apartments</option>
                        {reportApartments.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                      </select>
                    </div>
                    <div className="filter-group">
                      <label>From</label>
                      <input type="date" value={reportFromR} onChange={(e) => setReportFromR(e.target.value)} />
                    </div>
                    <div className="filter-group">
                      <label>To</label>
                      <input type="date" value={reportToR} onChange={(e) => setReportToR(e.target.value)} />
                    </div>
                    <button type="button" className="btn-primary" onClick={() => fetchReports()}>Update</button>
                    <button type="button" className="btn-ghost" onClick={exportReportCsv} disabled={reportsLoading || ((reportType === 'summary' || reportType === 'profit_loss') && !reportSummary) || (reportType === 'bookings' && !reportBookings.length) || (reportType === 'expenses' && !reportExpenses.length) || (reportType === 'purchases' && !reportPurchases.length) || (reportType === 'payroll' && (!reportPropertyId || !reportPayrollRows.length))}>
                      Export CSV
                    </button>
                    <FdPerPageField
                      id="dashboard-reports-per-page"
                      value={dashboardTablePerPage}
                      onChange={setDashboardTablePerPage}
                      maxAllowed={100}
                    />
                    <div className="toolbar-spacer" />
                    {reportsLoading && <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Loading…</span>}
                  </div>
                  <div className="table-wrap">
                    {(reportType === 'summary' || reportType === 'profit_loss') && reportSummary && (
                      reportType === 'profit_loss' ? (
                        <div className="owner-pl-preview">
                          <p className="muted" style={{ marginBottom: '1rem', fontSize: '0.875rem', maxWidth: '40rem' }}>
                            Revenue includes only paid, non-cancelled bookings whose stay starts in this range (same as Front Desk).
                            Expenses and purchases use transaction dates. Net = revenue − expenses − purchases.
                          </p>
                          <div className="owner-pl-summary">
                            <div className="owner-pl-row owner-pl-row--head"><span>Line</span><span>Amount (₦)</span></div>
                            <div className="owner-pl-row"><span>Income (paid bookings)</span><span>{Number(reportSummary.revenue || 0).toLocaleString()}</span></div>
                            <div className="owner-pl-row"><span>Less: expenses</span><span>−{Number(reportSummary.expenses_total || 0).toLocaleString()}</span></div>
                            <div className="owner-pl-row"><span>Less: purchases</span><span>−{Number(reportSummary.purchases_total || 0).toLocaleString()}</span></div>
                            <div className={`owner-pl-row owner-pl-row--net${Number(reportSummary.profit) < 0 ? ' owner-pl-row--neg' : ''}`}>
                              <span>Net income</span><span>{Number(reportSummary.profit || 0).toLocaleString()}</span>
                            </div>
                          </div>
                          <p className="muted" style={{ marginTop: '1rem', fontSize: '0.8125rem' }}>
                            Period: {reportSummary.from} → {reportSummary.to} · Paid bookings: {reportSummary.bookings_count ?? 0}
                          </p>
                        </div>
                      ) : (
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>From</th>
                            <th>To</th>
                            <th className="col-amount">Revenue (paid)</th>
                            <th className="col-num">Paid bookings</th>
                            <th className="col-amount">Expenses</th>
                            <th className="col-amount">Purchases</th>
                            <th className="col-amount">Profit</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>{reportSummary.from}</td>
                            <td>{reportSummary.to}</td>
                            <td className="col-amount">₦{Number(reportSummary.revenue || 0).toLocaleString()}</td>
                            <td className="col-num">{reportSummary.bookings_count ?? 0}</td>
                            <td className="col-amount">₦{Number(reportSummary.expenses_total || 0).toLocaleString()}</td>
                            <td className="col-amount">₦{Number(reportSummary.purchases_total || 0).toLocaleString()}</td>
                            <td className="col-amount">₦{Number(reportSummary.profit || 0).toLocaleString()}</td>
                          </tr>
                        </tbody>
                      </table>
                      )
                    )}
                    {reportType === 'bookings' && (
                      <>
                        <table className="data-table">
                          <thead><tr><th>Guest</th><th>Apartment</th><th>Dates</th><th className="col-center">Source</th><th className="col-center">Platform</th><th className="col-amount">Sub total</th><th className="col-amount">Caution</th><th className="col-amount">Total</th></tr></thead>
                          <tbody>
                            {reportBookings.length === 0 ? (
                              <tr><td colSpan={8} style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>{reportsLoading ? 'Loading…' : 'No bookings in range.'}</td></tr>
                            ) : paginatedReportBookings.map((b) => {
                              const rpb = getPricingBreakdown(b);
                              const rc = cautionToneClassName(rpb.tone, 'booking-caution-tone');
                              return (
                              <tr key={b.id}>
                                <td><span className="cell-primary">{b.user?.name || '—'}</span><span className="cell-muted">{b.user?.email}</span></td>
                                <td>{b.apartment?.name} {b.apartment?.property?.name && `(${b.apartment.property.name})`}</td>
                                <td>{formatDate(b.start_date)} – {formatDate(b.end_date)}</td>
                                <td className="col-center" style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>{formatBookingSource(b)}</td>
                                <td className="col-center" style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>{formatBookingPlatform(b)}</td>
                                <td className="col-amount">₦{Number(rpb.stay_subtotal).toLocaleString()}</td>
                                <td className={`col-amount ${rc}`}>₦{Number(rpb.caution_display).toLocaleString()}</td>
                                <td className="col-amount">₦{Number(rpb.total).toLocaleString()}</td>
                              </tr>
                            );})}
                          </tbody>
                        </table>
                        <PaginationFooter meta={reportBookingsMeta} onPageChange={setReportBookingsPage} loading={reportsLoading} />
                      </>
                    )}
                    {reportType === 'expenses' && (
                      <>
                        <table className="data-table">
                          <thead><tr><th>Date</th><th>Property</th><th>Category</th><th className="col-amount">Amount</th><th>Description</th></tr></thead>
                          <tbody>
                            {reportExpenses.length === 0 ? (
                              <tr><td colSpan={5} style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>{reportsLoading ? 'Loading…' : 'No expenses in range.'}</td></tr>
                            ) : paginatedReportExpenses.map((ex) => (
                              <tr key={ex.id}>
                                <td>{(ex.expense_date || '').slice(0, 10)}</td>
                                <td>{ex.property?.name || '—'}</td>
                                <td style={{ textTransform: 'capitalize' }}>{ex.category}</td>
                                <td className="col-amount">₦{Number(ex.amount).toLocaleString()}</td>
                                <td>{ex.description || '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        <PaginationFooter meta={reportExpensesMeta} onPageChange={setReportExpensesPage} loading={reportsLoading} />
                      </>
                    )}
                    {reportType === 'purchases' && (
                      <>
                        <table className="data-table">
                          <thead><tr><th>Date</th><th>Property</th><th>Item</th><th>Category</th><th className="col-num">Qty</th><th className="col-amount">Amount</th><th>Vendor</th></tr></thead>
                          <tbody>
                            {reportPurchases.length === 0 ? (
                              <tr><td colSpan={7} style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>{reportsLoading ? 'Loading…' : 'No purchases in range.'}</td></tr>
                            ) : paginatedReportPurchases.map((p) => (
                              <tr key={p.id}>
                                <td>{(p.purchase_date || '').slice(0, 10)}</td>
                                <td>{p.property?.name || '—'}</td>
                                <td>{p.item_name}</td>
                                <td style={{ textTransform: 'capitalize' }}>{p.category}</td>
                                <td className="col-num">{p.quantity}</td>
                                <td className="col-amount">₦{Number(p.amount).toLocaleString()}</td>
                                <td>{p.vendor || '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        <PaginationFooter meta={reportPurchasesMeta} onPageChange={setReportPurchasesPage} loading={reportsLoading} />
                      </>
                    )}
                    {reportType === 'payroll' && (
                      <>
                        {!reportPropertyId ? (
                          <p style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
                            Select a property to load payroll slips.
                          </p>
                        ) : (
                          <table className="data-table">
                            <thead>
                              <tr>
                                <th>Period</th>
                                <th>Staff</th>
                                <th className="col-amount">Gross</th>
                                <th className="col-amount">Net</th>
                                <th>Paid</th>
                                <th>Reference</th>
                                {hasPermission('payroll-manage') ? <th className="col-actions">Slip</th> : null}
                              </tr>
                            </thead>
                            <tbody>
                              {reportPayrollRows.length === 0 ? (
                                <tr>
                                  <td
                                    colSpan={hasPermission('payroll-manage') ? 7 : 6}
                                    style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}
                                  >
                                    {reportsLoading ? 'Loading…' : 'No payroll slips for this property.'}
                                  </td>
                                </tr>
                              ) : (
                                reportPayrollRows.map((pr) => (
                                  <tr key={pr.id}>
                                    <td>
                                      {pr.period_year}-{String(pr.period_month).padStart(2, '0')}
                                    </td>
                                    <td>
                                      <span className="cell-primary">{pr.staff_name}</span>
                                      <span className="cell-muted">{pr.staff_email}</span>
                                    </td>
                                    <td className="col-amount">₦{Number(pr.gross_amount_ngn).toLocaleString()}</td>
                                    <td className="col-amount">₦{Number(pr.net_amount_ngn).toLocaleString()}</td>
                                    <td>{pr.paid_at || '—'}</td>
                                    <td>{pr.payment_reference || '—'}</td>
                                    {hasPermission('payroll-manage') ? (
                                      <td className="col-actions">
                                        <button
                                          type="button"
                                          className="btn-icon"
                                          title="View PDF payslip"
                                          disabled={reportPayrollSlipLoadingId === pr.id}
                                          onClick={async () => {
                                            setError('');
                                            setReportPayrollSlipLoadingId(pr.id);
                                            try {
                                              const blob = await downloadOwnerPayrollSlip(pr.id);
                                              const how = openPdfBlobInNewTab(blob, { filename: `luxistt-payslip-${pr.id}.pdf` });
                                              setSuccessMessage(
                                                how === 'viewer'
                                                  ? 'Payslip opened in the PDF viewer.'
                                                  : 'Payslip saved to your device. Open the file to view or print.',
                                              );
                                              setTimeout(() => setSuccessMessage(''), 10_000);
                                            } catch (e) {
                                              setError(e.message || 'Could not open payslip');
                                            } finally {
                                              setReportPayrollSlipLoadingId(null);
                                            }
                                          }}
                                        >
                                          <FiDownload />
                                        </button>
                                      </td>
                                    ) : null}
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </section>
            )}

            {activeTab === 'users' && hasPermission('manage-users') && (
              <section className="dashboard-section dashboard-section-table">
                <div className="table-card">
                  <div className="table-toolbar">
                    <div className="filter-group">
                      <label htmlFor="users-search">
                        <FiSearch size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                        Search
                      </label>
                      <input
                        id="users-search"
                        type="search"
                        placeholder="Name, email or role…"
                        value={usersSearch}
                        onChange={(e) => setUsersSearch(e.target.value)}
                        aria-label="Search users"
                      />
                    </div>
                    <FdPerPageField
                      id="dashboard-users-per-page"
                      value={dashboardTablePerPage}
                      onChange={setDashboardTablePerPage}
                      maxAllowed={100}
                    />
                    <div className="toolbar-spacer" />
                    <button type="button" className="btn-add" onClick={openAddUser}>
                      <FiPlus /> Add user
                    </button>
                    {usersLoading && <span style={{ marginLeft: '0.75rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Loading…</span>}
                  </div>
                  <div className="table-wrap">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Role</th>
                          <th className="col-num">Properties</th>
                          <th className="col-actions">Actions</th>
                        </tr>
                      </thead>
                        <tbody>
                        {filteredUsers.length === 0 ? (
                          <tr>
                            <td colSpan={5} style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                              {usersLoading ? 'Loading…' : usersSearch.trim() ? 'No users match your search.' : 'No users.'}
                            </td>
                          </tr>
                        ) : (
                          paginatedUsers.map((u) => (
                            <tr key={u.id}>
                              <td><span className="cell-primary">{u.name}</span></td>
                              <td><span className="cell-muted">{u.email}</span></td>
                              <td><span className={`users-role-badge users-role-badge--${String(u.role || '').toLowerCase()}`}>{u.role || '—'}</span></td>
                              <td className="col-num">{u.owned_properties_count ?? (u.property_ids?.length ?? 0)}</td>
                              <td className="col-actions">
                                <button type="button" className="btn-action" onClick={() => openEditUser(u)} title="Edit user">
                                  <FiEdit2 size={16} /> Edit
                                </button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                  <PaginationFooter meta={usersMeta} onPageChange={setUsersPage} loading={usersLoading} />
                </div>
              </section>
            )}
          </>
        )}
        <footer className="dashboard-footer">
          &copy; {new Date().getFullYear()} Luxistt. All rights reserved. | Built with <span style={{ color: '#e25555' }}>&#10084;</span> by <a href="https://www.newistt.com" target="_blank" rel="noopener noreferrer">newistt</a>
        </footer>
        </div>
      </main>

      {showUserModal && (
        <div className="modal-backdrop" onClick={closeUserModal}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '480px' }}>
            <h3>{editingUser ? 'Edit user' : 'Add user'}</h3>
            {error && <div className="login-error" style={{ marginBottom: '1rem' }}>{error}</div>}
            <form onSubmit={handleSaveUser}>
              <label>Name</label>
              <input
                type="text"
                value={userForm.name}
                onChange={(e) => setUserForm((f) => ({ ...f, name: e.target.value }))}
                required
              />
              <label>Email</label>
              <input
                type="email"
                value={userForm.email}
                onChange={(e) => setUserForm((f) => ({ ...f, email: e.target.value }))}
                required
              />
              <label>Phone number</label>
              <input
                type="tel"
                value={userForm.phone_number}
                onChange={(e) => setUserForm((f) => ({ ...f, phone_number: e.target.value }))}
                required
                placeholder="+14155552671 or +442071838750"
                pattern="^\\+?[0-9\\s\\-()]{7,20}$"
                title="Use a valid international phone number"
              />
              <p className="cell-muted" style={{ fontSize: '0.75rem', margin: '-0.4rem 0 0.75rem' }}>
                Phone number is required, unique across all users, and should include country code.
              </p>
              {!editingUser && (
                <p className="cell-muted" style={{ fontSize: '0.8125rem', margin: '0 0 0.5rem' }}>
                  A welcome email with a set-password link will be sent to this address.
                </p>
              )}
              {editingUser && (
                <>
                  <label>New password (leave blank to keep)</label>
                  <input
                    type="password"
                    value={userForm.password}
                    onChange={(e) => setUserForm((f) => ({ ...f, password: e.target.value }))}
                    placeholder="Leave blank to keep"
                  />
                  <label>Confirm new password</label>
                  <input
                    type="password"
                    value={userForm.password_confirmation}
                    onChange={(e) => setUserForm((f) => ({ ...f, password_confirmation: e.target.value }))}
                    placeholder="Leave blank to keep"
                  />
                </>
              )}
              <label>Role</label>
              <select
                value={userForm.role_id}
                onChange={(e) => setUserForm((f) => ({ ...f, role_id: e.target.value }))}
                disabled={!!editingUser}
              >
                <option value={ROLE_ADMIN}>Admin</option>
                <option value={ROLE_OWNER}>Owner</option>
                <option value={ROLE_USER}>User</option>
              </select>
              {editingUser && <span className="cell-muted" style={{ fontSize: '0.75rem', display: 'block', marginTop: 2 }}>Role cannot be changed.</span>}
              {(Number(userForm.role_id) === ROLE_OWNER) && (
                <>
                  <label>Assign properties</label>
                  <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 6, padding: 8 }}>
                    {adminProperties.length === 0 ? (
                      <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--text-secondary)' }}>No properties. Create properties first.</p>
                    ) : (
                      adminProperties.map((p) => (
                        <label key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                          <input
                            type="checkbox"
                            checked={userForm.property_ids.includes(p.id)}
                            onChange={() => togglePropertyForUser(p.id)}
                          />
                          <span>{p.name}</span>
                          {p.owner?.name && <span className="cell-muted" style={{ fontSize: '0.75rem' }}>(current: {p.owner.name})</span>}
                        </label>
                      ))
                    )}
                  </div>
                </>
              )}
              <div className="modal-actions">
                <button type="button" className="btn-ghost" onClick={closeUserModal}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Saving…' : editingUser ? 'Update' : 'Create user'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {guestRefundModalBooking ? (
        <div
          className="modal-backdrop"
          role="presentation"
          onClick={() => {
            if (!guestRefundSaving) {
              setGuestRefundModalBooking(null);
              setGuestRefundAmount('');
              setGuestRefundNote('');
            }
          }}
        >
          <div className="modal-box" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '440px' }}>
            <h3>Refund guest</h3>
            <p style={{ margin: '0 0 1rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
              Booking #{guestRefundModalBooking.id} · collected ₦{Number(guestRefundModalBooking.total_price || 0).toLocaleString()}.
              This cancels the stay, marks payment refunded, and updates the total to the amount you retain.
            </p>
            {error ? <div className="login-error" style={{ marginBottom: '1rem' }}>{error}</div> : null}
            <label style={{ display: 'block', marginBottom: '0.75rem' }}>
              Refund amount (₦)
              <input
                type="text"
                inputMode="numeric"
                autoComplete="off"
                className="luxistt-money-input"
                value={guestRefundAmount}
                onChange={(e) => setGuestRefundAmount(formatWholeNairaInputChange(e.target.value))}
                placeholder={
                  guestRefundModalBooking.total_price != null &&
                  Number.isFinite(Number(guestRefundModalBooking.total_price))
                    ? `Max ${Number(guestRefundModalBooking.total_price).toLocaleString('en-NG')}`
                    : 'Amount'
                }
                style={{ width: '100%', marginTop: '0.35rem' }}
              />
            </label>
            <label style={{ display: 'block', marginBottom: '0.75rem' }}>
              Note (optional)
              <textarea
                value={guestRefundNote}
                onChange={(e) => setGuestRefundNote(e.target.value)}
                placeholder="e.g. partial refund agreed with guest…"
                maxLength={5000}
                rows={3}
                style={{ width: '100%', marginTop: '0.35rem', resize: 'vertical' }}
              />
            </label>
            <div className="modal-actions">
              <button
                type="button"
                className="btn-ghost"
                disabled={guestRefundSaving}
                onClick={() => {
                  setGuestRefundModalBooking(null);
                  setGuestRefundAmount('');
                  setGuestRefundNote('');
                }}
              >
                Cancel
              </button>
              <button type="button" className="btn-primary" disabled={guestRefundSaving} onClick={submitOwnerGuestRefund}>
                {guestRefundSaving ? 'Processing…' : 'Submit refund'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {ownerCautionModalBooking ? (
        <div
          className="modal-backdrop"
          role="presentation"
          onClick={() => {
            if (!ownerCautionSaving) {
              setOwnerCautionModalBooking(null);
              setOwnerCautionKind('refund');
              setOwnerCautionAmount('');
              setOwnerCautionNote('');
            }
          }}
        >
          <div className="modal-box" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '440px' }}>
            <h3>Caution fees</h3>
            <p style={{ margin: '0 0 1rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
              Booking #{ownerCautionModalBooking.id}. Adjust refundable caution or record forfeit / extra damage charges.
            </p>
            {error ? <div className="login-error" style={{ marginBottom: '1rem' }}>{error}</div> : null}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1rem' }}>
              <label style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start', fontSize: '0.875rem', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="owner-caution-kind"
                  checked={ownerCautionKind === 'refund'}
                  onChange={() => setOwnerCautionKind('refund')}
                  style={{ marginTop: '0.2rem' }}
                />
                Refund to guest (reduces total)
              </label>
              <label style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start', fontSize: '0.875rem', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="owner-caution-kind"
                  checked={ownerCautionKind === 'forfeit'}
                  onChange={() => setOwnerCautionKind('forfeit')}
                  style={{ marginTop: '0.2rem' }}
                />
                Forfeit / damage (can exceed remaining caution to add to total)
              </label>
            </div>
            <label style={{ display: 'block', marginBottom: '0.75rem' }}>
              Amount (₦)
              <input
                type="text"
                inputMode="numeric"
                autoComplete="off"
                className="luxistt-money-input"
                value={ownerCautionAmount}
                onChange={(e) => setOwnerCautionAmount(formatWholeNairaInputChange(e.target.value))}
                style={{ width: '100%', marginTop: '0.35rem' }}
              />
            </label>
            <label style={{ display: 'block', marginBottom: '0.75rem' }}>
              Note (optional)
              <textarea
                value={ownerCautionNote}
                onChange={(e) => setOwnerCautionNote(e.target.value)}
                maxLength={5000}
                rows={3}
                style={{ width: '100%', marginTop: '0.35rem', resize: 'vertical' }}
              />
            </label>
            <div className="modal-actions">
              <button
                type="button"
                className="btn-ghost"
                disabled={ownerCautionSaving}
                onClick={() => {
                  setOwnerCautionModalBooking(null);
                  setOwnerCautionKind('refund');
                  setOwnerCautionAmount('');
                  setOwnerCautionNote('');
                }}
              >
                Cancel
              </button>
              <button type="button" className="btn-primary" disabled={ownerCautionSaving} onClick={submitOwnerCautionAdjustment}>
                {ownerCautionSaving ? 'Saving…' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {showCreateBookingModal && (
        <div className="modal-backdrop" onClick={() => setShowCreateBookingModal(false)}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '420px' }}>
            <h3>Create booking (outside the website)</h3>
            <p style={{ margin: '0 0 1rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Record a booking that wasn’t made through Luxistt.com and the amount collected.</p>
            {error && <div className="login-error" style={{ marginBottom: '1rem' }}>{error}</div>}
            <form onSubmit={handleCreateBooking}>
              <label>Property</label>
              <select value={bookingForm.property_id} onChange={(e) => onBookingPropertyChange(e.target.value)} required>
                <option value="">Select property</option>
                {properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <label>Apartment</label>
              <select value={bookingForm.apartment_id} onChange={(e) => setBookingForm((f) => ({ ...f, apartment_id: e.target.value }))} required>
                <option value="">Select apartment</option>
                {apartmentsForBooking.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
              <label>Check-in</label>
              <input type="date" value={bookingForm.start_date} onChange={(e) => setBookingForm((f) => ({ ...f, start_date: e.target.value }))} required />
              <label>Check-out</label>
              <input type="date" value={bookingForm.end_date} onChange={(e) => setBookingForm((f) => ({ ...f, end_date: e.target.value }))} required />
              <label>Guests (adults)</label>
              <input type="number" min={1} value={bookingForm.guests_adults} onChange={(e) => setBookingForm((f) => ({ ...f, guests_adults: e.target.value }))} />
              <label>Guests (children)</label>
              <input type="number" min={0} value={bookingForm.guests_children} onChange={(e) => setBookingForm((f) => ({ ...f, guests_children: e.target.value }))} />
              <label>Platform</label>
              <select value={bookingForm.booking_platform_id} onChange={(e) => setBookingForm((f) => ({ ...f, booking_platform_id: e.target.value }))} aria-label="Booking platform" required>
                {bookingPlatforms.length === 0 ? (
                  <option value="">Loading platforms…</option>
                ) : (
                  bookingPlatforms.map((p) => (
                    <option key={p.id} value={String(p.id)}>{p.name}</option>
                  ))
                )}
              </select>
              <label>Amount collected (₦)</label>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="off"
                className="luxistt-money-input"
                value={bookingForm.total_price}
                onChange={(e) =>
                  setBookingForm((f) => ({ ...f, total_price: formatWholeNairaInputChange(e.target.value) }))
                }
                placeholder="e.g. 850,000"
                required
              />
              <div className="modal-actions">
                <button type="button" className="btn-ghost" onClick={() => setShowCreateBookingModal(false)}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Create booking'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showPropertyModal && (
        <div className="modal-backdrop" onClick={() => setShowPropertyModal(false)}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()}>
            <h3>Add property</h3>
            {error && <div className="login-error" style={{ marginBottom: '1rem' }}>{error}</div>}
            <form onSubmit={handleAddProperty}>
              <label>Name</label>
              <input
                type="text"
                value={propertyForm.name}
                onChange={(e) => setPropertyForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Property name"
                required
              />
              <label>City</label>
              <select
                value={propertyForm.city_id}
                onChange={(e) => setPropertyForm((f) => ({ ...f, city_id: e.target.value }))}
                required
              >
                <option value="">Select city</option>
                {cities.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
              <label>Street address</label>
              <input
                type="text"
                value={propertyForm.address_street}
                onChange={(e) => setPropertyForm((f) => ({ ...f, address_street: e.target.value }))}
                placeholder="Street address"
                required
              />
              <label>Postcode (optional)</label>
              <input
                type="text"
                value={propertyForm.address_postcode}
                onChange={(e) => setPropertyForm((f) => ({ ...f, address_postcode: e.target.value }))}
                placeholder="Postcode"
              />
              <div className="modal-actions">
                <button type="button" className="btn-ghost" onClick={() => setShowPropertyModal(false)}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Add property'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
